import type { MouseEventHandler } from 'react';

import { useTranslation } from '~ai-tutor/lib/i18n';

import HaufeGroupLogo from './logo/HaufeGroupLogo';
import SocialmediaLogo, { type brandTypes } from './logo/SocialmediaLogo';
import { Button } from './ui/button';

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    UC_UI?: {
      showSecondLayer?: () => void;
      updateLanguage?: (language: string) => void;
    };
  }
}

const onCookiesLinkClick: MouseEventHandler<HTMLAnchorElement> & MouseEventHandler<HTMLSpanElement> = () => {
  // eslint-disable-next-line unicorn-x/prefer-global-this -- UC_UI is attached to window by Usercentrics
  const ucUi = window.UC_UI;
  if (ucUi?.showSecondLayer) {
    ucUi.showSecondLayer();
    return;
  }

  // CMP may still be loading after client-only injection
  const onReady = () => {
    // eslint-disable-next-line unicorn-x/prefer-global-this
    window.UC_UI?.showSecondLayer?.();
    globalThis.removeEventListener('UC_UI_INITIALIZED', onReady);
  };
  globalThis.addEventListener('UC_UI_INITIALIZED', onReady);
};

export default function Footer() {
  const { t } = useTranslation();

  const links = [
    { href: t('footer.agbHref'), label: t('footer.agb') },
    { href: t('footer.impressumHref'), label: t('footer.impressum') },
    { href: t('footer.privacyHref'), label: t('footer.privacy') },
  ];

  const socialLinks = [
    { icon: 'linkedin', href: 'https://www.linkedin.com/company/haufe-akademie/', label: 'LinkedIn' },
    { icon: 'instagram', href: 'https://www.instagram.com/haufe.akademie/', label: 'Instagram' },
    { icon: 'facebook', href: 'https://www.facebook.com/HaufeAkademie', label: 'Facebook' },
    { icon: 'tiktok', href: 'https://www.tiktok.com/@haufe.akademie', label: 'TikTok' },
    { icon: 'youtube', href: 'https://www.youtube.com/user/HaufeAkademieTV', label: 'YouTube' },
    { icon: 'xing', href: 'https://www.xing.com/pages/haufeakademiegmbh-co-kg', label: 'Xing' },
  ];

  return (
    <footer className="bg-foreground text-background mt-auto">
      <div className="container mx-auto max-w-7xl px-20 py-20 lg:px-4">
        <div className="grid grid-cols-1 gap-20 lg:grid-cols-[2fr_1fr_1fr] lg:gap-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:gap-16">
            {links.map((link) => {
              return (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={link.label}
                  className="label-default text-background hover:text-primary-foreground-hover transition-colors duration-200"
                >
                  {link.label}
                </a>
              );
            })}
            <Button
              variant="link"
              onClick={onCookiesLinkClick}
              rel="noopener noreferrer"
              className="label-default text-background hover:text-primary-foreground-hover self-start p-0 transition-colors duration-200 hover:no-underline sm:self-auto"
            >
              {t('footer.cookies')}
            </Button>
          </div>
          <div className="flex items-center justify-center gap-8">
            <div className="label-default text-background/60">{t('footer.brandText')}</div>
            <HaufeGroupLogo />
          </div>

          <div className="flex flex-col gap-4 lg:items-end">
            <div className="flex flex-wrap items-center justify-center gap-12">
              {socialLinks.map((social) => {
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="label-default text-background hover:text-primary-foreground-hover transition-colors duration-200"
                  >
                    <SocialmediaLogo
                      brand={social.icon as brandTypes}
                      className="max-h-20 max-w-20 transition-opacity duration-200 hover:opacity-80"
                    />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
